import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import LogoMark from "@/components/LogoMark";

/* ---------- Hero visual: the brand hologram core (real render + parallax) ---------- */

export const HeroVisual = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });

  useEffect(() => {
    if (reduce) return;
    const onMove = (e) => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      mx.set(((e.clientX - r.left) / r.width - 0.5) * 18);
      my.set(((e.clientY - r.top) / r.height - 0.5) * 14);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [reduce, mx, my]);

  useEffect(() => {
    if (reduce) return;
    const onLeave = () => { mx.set(0); my.set(0); };
    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => document.documentElement.removeEventListener("mouseleave", onLeave);
  }, [reduce, mx, my]);

  return (
    <div ref={ref} className="relative w-full max-w-[520px] md:max-w-none select-none">
      {/* soft bloom behind the render so it sits into the page */}
      <div className="pointer-events-none absolute inset-[6%] rounded-full bg-[radial-gradient(circle_at_50%_45%,rgba(127,208,230,0.28),transparent_65%)]" />

      <motion.div style={reduce ? undefined : { x: sx, y: sy }} className="relative">
        <motion.img
          src="/ai-core.png"
          alt="Glowing holographic AI core — AidaNova mark inside a glass cube"
          draggable={false}
          animate={reduce ? {} : { y: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="h-auto w-full [mask-image:radial-gradient(ellipse_64%_60%_at_54%_46%,black_44%,transparent_78%)]"
        />
      </motion.div>
    </div>
  );
};

/* ---------- "Accessible AI" visual — one core reaching people (Why We Exist) ---------- */

const PEOPLE = [
  { x: 52, y: 64, d: "M200 150 Q 118 96 56 66", b: "0s" },
  { x: 200, y: 32, d: "M200 150 Q 200 90 200 38", b: "0.5s" },
  { x: 348, y: 64, d: "M200 150 Q 282 96 344 66", b: "1s" },
  { x: 52, y: 234, d: "M200 150 Q 118 204 56 232", b: "0.25s" },
  { x: 200, y: 266, d: "M200 150 Q 200 210 200 260", b: "0.75s" },
  { x: 348, y: 234, d: "M200 150 Q 282 204 344 232", b: "1.25s" },
];

const PersonNode = ({ x, y }) => (
  <g transform={`translate(${x} ${y})`}>
    <circle r="16" fill="#FFFFFF" stroke="#43B0CE" strokeWidth="1.5" />
    <circle r="16" fill="none" stroke="#7FD0E6" strokeWidth="4" opacity="0.35" filter="url(#bloom3)" />
    <circle cy="-3" r="2.8" fill="#2E96B0" />
    <path d="M-4.6 5.4 C-4.6 2 -2.5 0.9 0 0.9 C2.5 0.9 4.6 2 4.6 5.4 Z" fill="#2E96B0" />
  </g>
);

export const AccessVisual = () => {
  const reduce = useReducedMotion();
  return (
    <div
      className="relative h-64 w-full overflow-hidden bg-[radial-gradient(circle_at_50%_45%,rgba(127,208,230,0.28),rgba(237,248,248,0.9))]"
      aria-hidden="true"
    >
      {/* dotted texture */}
      <div className="absolute inset-0 bg-[radial-gradient(rgba(67,176,206,0.12)_1px,transparent_1px)] bg-[size:18px_18px]" />

      <svg viewBox="0 0 400 300" className="h-full w-full">
        <defs>
          <filter id="bloom3" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
          <radialGradient id="accessCoreGlow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="rgba(191,233,244,0.95)" />
            <stop offset="50%" stopColor="rgba(127,208,230,0.5)" />
            <stop offset="100%" stopColor="rgba(127,208,230,0)" />
          </radialGradient>
        </defs>

        {/* connection lines: soft glow + clean stroke */}
        {PEOPLE.map(({ d }, i) => (
          <g key={i}>
            <path d={d} fill="none" stroke="#7FD0E6" strokeWidth="4" opacity="0.3" filter="url(#bloom3)" />
            <path d={d} fill="none" stroke="rgba(67,176,206,0.45)" strokeWidth="1.4" />
          </g>
        ))}

        {/* light pulses travelling core → people */}
        {PEOPLE.map(({ d, b }, i) => (
          <g key={`p${i}`}>
            <circle r="5" fill="#7FD0E6" opacity="0.45" filter="url(#bloom3)">
              <animateMotion dur="2.8s" begin={b} repeatCount="indefinite" path={d} />
            </circle>
            <circle r="2.6" fill="#2E96B0">
              <animateMotion dur="2.8s" begin={b} repeatCount="indefinite" path={d} />
            </circle>
          </g>
        ))}

        {/* people nodes */}
        {PEOPLE.map(({ x, y }, i) => (
          <PersonNode key={i} x={x} y={y} />
        ))}

        {/* radiant AI core */}
        <motion.g
          style={{ transformOrigin: "200px 150px" }}
          animate={reduce ? {} : { scale: [1, 1.07, 1] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        >
          <circle cx="200" cy="150" r="48" fill="url(#accessCoreGlow)" />
          <circle cx="200" cy="150" r="24" fill="#BFE9F4" opacity="0.85" filter="url(#bloom3)" />
          <circle cx="200" cy="150" r="17" fill="#FFFFFF" stroke="#7FD0E6" strokeWidth="1.5" />
        </motion.g>
      </svg>

      {/* brand mark at the core */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <LogoMark className="h-7 w-7 drop-shadow-[0_0_12px_rgba(67,176,206,0.8)]" />
      </div>
    </div>
  );
};

/* ---------- product visuals for "What We Build" tabs ---------- */

const ChatBubble = ({ x, y, w, delay, dark = false }) => (
  <motion.rect
    x={x}
    y={y}
    rx="7"
    height="16"
    width={w}
    fill={dark ? "rgba(67,176,206,0.85)" : "rgba(255,255,255,0.9)"}
    stroke={dark ? "none" : "rgba(11,36,41,0.1)"}
    animate={{ opacity: [0.35, 1, 0.35] }}
    transition={{ duration: 3, repeat: Infinity, delay }}
  />
);

export const TabVisual = ({ variant = "experiences" }) => (
  <div
    className="relative h-56 w-full overflow-hidden bg-[radial-gradient(circle_at_30%_20%,rgba(67,176,206,0.14),rgba(237,248,248,0.5))] sm:h-64"
    aria-hidden="true"
  >
    {variant === "experiences" && (
      <svg viewBox="0 0 320 220" className="h-full w-full">
        <rect x="70" y="28" width="180" height="164" rx="14" fill="#FFFFFF" stroke="rgba(11,36,41,0.12)" />
        <rect x="70" y="28" width="180" height="26" rx="14" fill="#F1FAFB" />
        <circle cx="86" cy="41" r="3" fill="#43B0CE" />
        <circle cx="97" cy="41" r="3" fill="#7FD0E6" />
        <circle cx="108" cy="41" r="3" fill="#C5E7EF" />
        <ChatBubble x={86} y={70} w={110} delay={0} />
        <ChatBubble x={124} y={96} w={110} delay={0.7} dark />
        <ChatBubble x={86} y={122} w={90} delay={1.4} />
        <motion.g animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <circle cx="90" cy="158" r="3" fill="#43B0CE" />
          <circle cx="101" cy="158" r="3" fill="#43B0CE" />
          <circle cx="112" cy="158" r="3" fill="#43B0CE" />
        </motion.g>
      </svg>
    )}

    {variant === "workflows" && (
      <svg viewBox="0 0 320 220" className="h-full w-full">
        {["M60 110 L120 60 L190 60", "M120 160 L190 160 L250 110", "M190 60 L250 110"].map((d, i) => (
          <motion.path key={d} d={d} fill="none" stroke="rgba(67,176,206,0.5)" strokeWidth="1.6" strokeDasharray="5 6"
            animate={{ strokeDashoffset: [0, -44] }} transition={{ duration: 4 + i, repeat: Infinity, ease: "linear" }} />
        ))}
        {[[60, 110], [120, 60], [120, 160], [190, 60], [190, 160], [250, 110]].map(([x, y], i) => (
          <motion.circle key={i} cx={x} cy={y} r="9" fill="#FFFFFF" stroke="#43B0CE" strokeWidth="1.6"
            animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 2.6, repeat: Infinity, delay: i * 0.4 }}
            style={{ transformOrigin: `${x}px ${y}px` }} />
        ))}
        <motion.circle r="4" fill="#43B0CE"
          animate={{ cx: [60, 120, 190, 250], cy: [110, 60, 60, 110] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }} />
      </svg>
    )}

    {variant === "pipeline" && (
      <svg viewBox="0 0 320 220" className="h-full w-full">
        {/* sketch */}
        <motion.rect x="42" y="70" width="80" height="80" rx="10" fill="none" stroke="#64787E" strokeWidth="1.5" strokeDasharray="6 5"
          animate={{ strokeDashoffset: [0, -44] }} transition={{ duration: 5, repeat: Infinity, ease: "linear" }} />
        <motion.path d="M52 118 L74 92 L92 108" fill="none" stroke="#64787E" strokeWidth="1.5" strokeLinecap="round"
          animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 3, repeat: Infinity }} />
        {/* arrow */}
        <motion.path d="M136 110 L178 110" fill="none" stroke="#43B0CE" strokeWidth="2" strokeLinecap="round" strokeDasharray="5 5"
          animate={{ strokeDashoffset: [0, -20] }} transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }} />
        <path d="M172 103 L182 110 L172 117" fill="none" stroke="#43B0CE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* working app */}
        <motion.g animate={{ y: [0, -5, 0] }} transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}>
          <rect x="196" y="62" width="86" height="96" rx="12" fill="#FFFFFF" stroke="rgba(67,176,206,0.6)" />
          <rect x="196" y="62" width="86" height="20" rx="12" fill="#E4F3F7" />
          <circle cx="209" cy="72" r="2.6" fill="#43B0CE" />
          <rect x="207" y="92" width="64" height="9" rx="4.5" fill="#D5EDF3" />
          <rect x="207" y="108" width="46" height="9" rx="4.5" fill="#E9F6F9" />
          <rect x="207" y="128" width="34" height="14" rx="7" fill="#43B0CE" />
        </motion.g>
      </svg>
    )}
  </div>
);
